import {
  Button,
  Cell,
  Column,
  MenuTrigger,
  Row,
  Stack,
  Table,
  TableBody,
  TableFooter,
  TableHeader,
} from '@eleveo/components-react';
import { EyeIcon, ThreeDotsIcon } from '@eleveo/icons-react';
// eslint-disable-next-line import/no-extraneous-dependencies
import { parseAbsoluteToLocal } from '@internationalized/date';
import { symmetricDifference } from 'ramda';
import React from 'react';
// eslint-disable-next-line import/no-extraneous-dependencies
import { useDateFormatter } from 'react-aria';
import { useTranslation } from 'react-i18next';

import { useAuth } from '../../app/AuthorizationProvider';
import { RequestStatusComponent, RequestSubtypeComponent } from '../../components/AgentsRequests';
import { AvatarLabel } from '../../components/LayoutNext/components/AvatarLabel';
import { MultipleItemsLabel } from '../../components/LayoutNext/components/MultipleItemsLabel';
import {
  AgentShiftWithoutItems,
  Queue,
  RequestState,
  RequestType,
  SupervisorRequestPreview,
} from '../../graphql/components';
import { formatNameOfPerson } from '../../utils/helpers';
import { extractRequestIdAndTypeFromKey, getRequestKey } from '../Scheduling/AgentsView/utils';
import { LIST_CONTAINER_TAG, getListItemTag } from './constants';

type RequestsTableProps = {
  activeId?: string;
  children: React.ReactNode;
  onView: (requestId: string, requestType: RequestType) => void;
  queues: Record<string, Queue>;
  supervisorRequests: SupervisorRequestPreview[];
};

export const RequestsTable = ({ activeId, children, onView, supervisorRequests, queues }: RequestsTableProps) => {
  const { timezone } = useAuth();
  const { t } = useTranslation();

  const formatter = useDateFormatter({
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    timeZone: timezone,
  });

  const columns = [
    {
      key: 'name',
      name: t('person.name'),
    },
    {
      key: 'queues',
      name: t('queue.queues'),
    },
    {
      key: 'requestType',
      name: t('schedule.request.requestType.requestType'),
    },
    {
      key: 'created',
      name: t('date.created'),
    },
    {
      key: 'status',
      name: t('dataImport.status.status'),
    },
    {
      key: 'requested',
      name: t('schedule.request.requested'),
    },
    {
      key: 'actions',
      name: t('layout.action.actions'),
      isActions: true,
      hideHeader: true,
    },
  ];

  const rows = supervisorRequests.map((supervisorRequest) => ({
    id: supervisorRequest.id,
    key: getRequestKey(supervisorRequest),
    name:
      supervisorRequest.type === RequestType.ShiftSwap ? (
        <Stack direction="vertical">
          <AvatarLabel
            name={formatNameOfPerson(
              supervisorRequest.requested.agent.firstName,
              supervisorRequest.requested.agent.lastName
            )}
            isColorized
            testId="shift-swap-request:requester:name"
          />
          <AvatarLabel
            name={formatNameOfPerson(
              (supervisorRequest.offered as AgentShiftWithoutItems).agent.firstName,
              (supervisorRequest.offered as AgentShiftWithoutItems).agent.lastName
            )}
            isColorized
            testId="shift-swap-request:recipient:name"
          />
        </Stack>
      ) : (
        <AvatarLabel
          name={formatNameOfPerson(
            supervisorRequest.requested.agent.firstName,
            supervisorRequest.requested.agent.lastName
          )}
          isColorized
        />
      ),
    queues:
      // show two lists of queues only in case that shift request's requester and recipient has different queues set
      supervisorRequest.type === RequestType.ShiftSwap &&
      symmetricDifference(
        supervisorRequest.requested.agent.queueIds ?? [],
        (supervisorRequest.offered as AgentShiftWithoutItems).agent.queueIds ?? []
      ).length ? (
        <Stack className="w-full" direction="vertical" spacing={20}>
          <MultipleItemsLabel
            items={supervisorRequest.requested.agent.queueIds?.map((queueId) => queues[queueId])}
            testId="shift-swap-request:requester:queues"
          />
          <MultipleItemsLabel
            items={(supervisorRequest.offered as AgentShiftWithoutItems).agent.queueIds?.map(
              (queueId) => queues[queueId]
            )}
            testId="shift-swap-request:recipient:queues"
          />
        </Stack>
      ) : (
        <MultipleItemsLabel items={supervisorRequest.requested.agent.queueIds?.map((queueId) => queues[queueId])} />
      ),
    requestType: <RequestSubtypeComponent type={supervisorRequest.subtype} />,
    created: formatter.format(parseAbsoluteToLocal(supervisorRequest.createdAt).toDate()),
    requested:
      supervisorRequest.type === RequestType.ShiftSwap ? (
        <Stack direction="vertical" spacing={20}>
          <div data-test-id="shift-swap-request:requester:shift-range">
            {formatter.format(parseAbsoluteToLocal(supervisorRequest.requested.dateRange.from).toDate())} -{' '}
            {formatter.format(parseAbsoluteToLocal(supervisorRequest.requested.dateRange.to).toDate())}
          </div>
          <div data-test-id="shift-swap-request:recipient:shift-range">
            {formatter.format(
              parseAbsoluteToLocal((supervisorRequest.offered as AgentShiftWithoutItems).dateRange.from).toDate()
            )}{' '}
            -{' '}
            {formatter.format(
              parseAbsoluteToLocal((supervisorRequest.offered as AgentShiftWithoutItems).dateRange.to).toDate()
            )}
          </div>
        </Stack>
      ) : (
        <>
          {formatter.format(parseAbsoluteToLocal(supervisorRequest.requested.dateRange.from).toDate())} -{' '}
          {formatter.format(parseAbsoluteToLocal(supervisorRequest.requested.dateRange.to).toDate())}
        </>
      ),
    status: <RequestStatusComponent status={supervisorRequest.state as unknown as RequestState} />,
    actions: (
      <MenuTrigger
        items={[
          {
            id: '1',
            icon: <EyeIcon />,
            onClick: () => onView(supervisorRequest.id, supervisorRequest.type),
            testId: getListItemTag('view'),
            text: t('layout.action.view'),
          },
        ]}
      >
        <Button aria-label={t('layout.action.actions')} isSmall testId={getListItemTag('actions')} variant="quiet">
          <ThreeDotsIcon />
        </Button>
      </MenuTrigger>
    ),
  }));

  return (
    <Table
      aria-label={t('requests.request', { count: 2 })}
      highlightedKey={activeId}
      isStickyHeader
      onAction={(key) => onView(...extractRequestIdAndTypeFromKey(key))}
      testId={LIST_CONTAINER_TAG}
    >
      <TableHeader columns={columns}>
        {({ key, name, ...columnProps }) => (
          <Column key={key} {...columnProps}>
            {name}
          </Column>
        )}
      </TableHeader>
      <TableBody items={rows}>
        {(item) => <Row key={item.key}>{(columnKey) => <Cell>{item[columnKey]}</Cell>}</Row>}
      </TableBody>
      <TableFooter>{children}</TableFooter>
    </Table>
  );
};
